import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';
import AWS_CONFIG from './config';

const TOKEN_KEYS = {
  accessToken: 'binyuma_access_token',
  idToken: 'binyuma_id_token',
  refreshToken: 'binyuma_refresh_token',
  expiresAt: 'binyuma_expires_at',
};

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

const isSecureStoreAvailable = Platform.OS === 'ios' || Platform.OS === 'android';

type SessionExpiredListener = () => void;
const sessionExpiredListeners = new Set<SessionExpiredListener>();

function notifySessionExpired() {
  sessionExpiredListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // Ignore listener errors
    }
  });
}

export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener);
  return () => {
    sessionExpiredListeners.delete(listener);
  };
}

async function secureGet(key: string): Promise<string | null> {
  if (!isSecureStoreAvailable) {
    const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
    return AsyncStorage.getItem(key);
  }
  return SecureStore.getItemAsync(key);
}

async function secureSet(key: string, value: string): Promise<void> {
  if (!isSecureStoreAvailable) {
    const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
    await AsyncStorage.setItem(key, value);
    return;
  }
  await SecureStore.setItemAsync(key, value);
}

async function secureDelete(key: string): Promise<void> {
  if (!isSecureStoreAvailable) {
    const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
    await AsyncStorage.removeItem(key);
    return;
  }
  await SecureStore.deleteItemAsync(key);
}

async function getStoredTokens(): Promise<AuthTokens | null> {
  try {
    const [accessToken, idToken, refreshToken, expiresAt] = await Promise.all([
      secureGet(TOKEN_KEYS.accessToken),
      secureGet(TOKEN_KEYS.idToken),
      secureGet(TOKEN_KEYS.refreshToken),
      secureGet(TOKEN_KEYS.expiresAt),
    ]);
    if (!accessToken) return null;
    return {
      accessToken,
      idToken: idToken || '',
      refreshToken: refreshToken || '',
      expiresAt: expiresAt ? parseInt(expiresAt, 10) : 0,
    };
  } catch {
    return null;
  }
}

export async function storeTokens(tokens: AuthTokens): Promise<void> {
  try {
    await Promise.all([
      secureSet(TOKEN_KEYS.accessToken, tokens.accessToken),
      secureSet(TOKEN_KEYS.idToken, tokens.idToken),
      secureSet(TOKEN_KEYS.refreshToken, tokens.refreshToken),
      secureSet(TOKEN_KEYS.expiresAt, String(tokens.expiresAt)),
    ]);
  } catch {
    // Silent failure -- token storage is best-effort
  }
}

export async function clearTokens(): Promise<void> {
  try {
    await Promise.all([
      secureDelete(TOKEN_KEYS.accessToken),
      secureDelete(TOKEN_KEYS.idToken),
      secureDelete(TOKEN_KEYS.refreshToken),
      secureDelete(TOKEN_KEYS.expiresAt),
    ]);
  } catch {
    // Silent failure
  }
}

/** Returns true if we have stored tokens (so /auth/me might succeed). Use to avoid calling API on focus when clearly not logged in. */
export async function hasStoredSession(): Promise<boolean> {
  const tokens = await getStoredTokens();
  return !!tokens?.accessToken;
}

async function refreshAccessToken(refreshToken: string): Promise<AuthTokens | null> {
  try {
    const response = await fetch(`${AWS_CONFIG.apiEndpoint}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        refreshToken,
        clientId: AWS_CONFIG.cognito.clientId,
      }),
    });

    if (!response.ok) {
      // If the refresh token is invalid/expired, clear stored tokens so the app
      // can treat the user as signed out and prompt for re-authentication.
      await clearTokens();
      notifySessionExpired();
      return null;
    }

    const data = await response.json();
    const newTokens: AuthTokens = {
      accessToken: data.accessToken,
      idToken: data.idToken,
      refreshToken: data.refreshToken || refreshToken,
      expiresAt: Date.now() + (data.expiresIn || 3600) * 1000,
    };

    await storeTokens(newTokens);
    return newTokens;
  } catch {
    // Network/other failure refreshing the token – clear tokens so subsequent
    // requests don't keep retrying with a bad refresh token.
    await clearTokens();
    notifySessionExpired();
    return null;
  }
}

async function getValidAccessToken(): Promise<string | null> {
  const tokens = await getStoredTokens();
  if (!tokens) return null;

  if (Date.now() < tokens.expiresAt - 60000) {
    return tokens.accessToken;
  }

  const newTokens = await refreshAccessToken(tokens.refreshToken);
  return newTokens?.accessToken ?? null;
}

export interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  status: number;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: Record<string, unknown> | FormData;
  authenticated?: boolean;
  headers?: Record<string, string>;
  timeout?: number;
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> {
  const {
    method = 'GET',
    body,
    authenticated = true,
    headers: customHeaders = {},
    timeout = 30000,
  } = options;

  const url = `${AWS_CONFIG.apiEndpoint}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };

  if (authenticated) {
    const token = await getValidAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    if (__DEV__) console.log(`API ${method} ${path}`);
    const fetchOptions: RequestInit = {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    };
    if (Platform.OS === 'web') {
      fetchOptions.credentials = 'include';
    }
    const response = await fetch(url, fetchOptions);

    clearTimeout(timeoutId);

    const responseData = await response.json().catch(() => null);

    if (!response.ok) {
      const errorMessage = responseData?.message || `HTTP ${response.status}`;
      if (__DEV__) console.log(`API Error ${path}:`, errorMessage);
      return { data: null, error: errorMessage, status: response.status };
    }

    return { data: responseData as T, error: null, status: response.status };
  } catch (error: unknown) {
    clearTimeout(timeoutId);
    const errorMessage = error instanceof Error ? error.message : 'Network error';
    if (__DEV__) console.log(`API Network Error ${path}:`, errorMessage);
    return { data: null, error: errorMessage, status: 0 };
  }
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method'>) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: Record<string, unknown>, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: Record<string, unknown>, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: Record<string, unknown>, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method'>) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
};

export default api;
